import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, thumbUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Plus, FileText, Trash2, Users, Sparkles, ShieldCheck, Pencil, Download, Filter } from "lucide-react";
import { toast } from "sonner";
import PendingApproval from "../components/PendingApproval";
import { useI18n, attrLabel } from "../lib/i18n";

function CaseCard({ c, kind, onDelete, canDelete, selectMode, isSelected, onToggleSelect }) {
  const { t } = useI18n();
  const title = kind === "ante_mortem" ? c.full_name : (c.reference_code || "Unidentified record");
  return (
    <div className={`flex gap-4 rounded-lg border bg-white p-4 shadow-sm transition-base hover:border-[#2E3A46]/40 ${isSelected ? "border-[#9E4747] ring-1 ring-[#9E4747]/40" : "border-[#E2E8F0]"}`} data-testid={`case-card-${c.id}`}>
      {selectMode && canDelete && (
        <div className="flex items-center">
          <Checkbox checked={!!isSelected} onCheckedChange={() => onToggleSelect(c.id)} data-testid={`case-select-${c.id}`} />
        </div>
      )}
      <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-md border border-[#E2E8F0] bg-[#F8F9FA]">
        {(c.photo_path || c.photo_paths?.[0]) ? (
          <img src={thumbUrl(c.photo_path || c.photo_paths[0])} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full w-full place-items-center text-[#6B7F82]">
            <FileText size={20} />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <Link to={`/${kind === "ante_mortem" ? "ante-mortem" : "post-mortem"}/${c.id}`} data-testid={`case-view-${c.id}`}>
              <h3 className="truncate font-heading text-base font-semibold text-[#1A2024] hover:text-[#2E3A46] hover:underline" data-testid={`case-title-${c.id}`}>{title}</h3>
            </Link>
            {c.reference_code && (
              <Link
                to={`/${kind === "ante_mortem" ? "ante-mortem" : "post-mortem"}/${c.id}`}
                className="font-mono text-[11px] text-[#6B7F82] hover:text-[#2E3A46] hover:underline"
                data-testid={`case-ref-${c.id}`}
              >
                {c.reference_code}
              </Link>
            )}
            <p className="font-body text-xs text-[#5C6873]">
              {c.country}{c.location ? `, ${c.location}` : ""} · {c.event_date || "date unknown"}
            </p>
          </div>
          <Badge className="rounded-full border border-[#E2E8F0] bg-[#F8F9FA] text-[#1A2024]" variant="outline">
            {c.status || "open"}
          </Badge>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {c.gender && c.gender !== "unknown" && <Tag>{t("form.gender." + c.gender)}</Tag>}
          {(c.age_min || c.age_max) && <Tag>{`age ${c.age_min ?? "?"}-${c.age_max ?? "?"}`}</Tag>}
          {c.hair_color && <Tag>{t("tag.hair")}: {attrLabel(t, c.hair_color)}</Tag>}
          {c.eye_color && <Tag>{t("tag.eyes")}: {attrLabel(t, c.eye_color)}</Tag>}
        </div>
        <p className="mt-2 line-clamp-2 font-body text-sm text-[#5C6873]">{c.description}</p>

        {canDelete && (
          <div className="mt-3 flex justify-end gap-2">
            <Link to={`/${kind === "ante_mortem" ? "ante-mortem" : "post-mortem"}/${c.id}/edit`} data-testid={`case-edit-${c.id}`}>
              <Button variant="outline" size="sm" className="border-[#E2E8F0] text-[#2E3A46]">
                <Pencil size={14} className="mr-1" /> {t("common.edit")}
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              className="text-[#9E4747] hover:bg-[#9E4747]/10"
              onClick={() => onDelete(c.id)}
              data-testid={`case-delete-${c.id}`}
            >
              <Trash2 size={14} className="mr-1" /> {t("common.delete")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Tag({ children }) {
  return (
    <span className="rounded-full border border-[#E2E8F0] bg-[#F8F9FA] px-2 py-0.5 font-body text-[11px] text-[#5C6873]">
      {children}
    </span>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [anteList, setAnteList] = useState([]);
  const [postList, setPostList] = useState([]);
  const [matchCount, setMatchCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [myImports, setMyImports] = useState(null);
  const [mineOnly, setMineOnly] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [accounts, setAccounts] = useState([]);

  const isPendingAuthority = user.role === "authority" && user.status !== "active";

  const refresh = async () => {
    if (isPendingAuthority) return;
    try {
      if (user.role === "admin") {
        const [post, ante, m, pc, acc] = await Promise.all([
          api.get("/post-mortem"),
          api.get("/ante-mortem"),
          api.get("/matches/count"),
          api.get("/admin/pending-count"),
          api.get("/admin/accounts"),
        ]);
        setPostList(post.data);
        setAnteList(ante.data);
        setMatchCount(m.data.count);
        setPendingCount(pc.data.pending);
        setAccounts(acc.data.filter((a) => a.case_count > 0));
      } else if (user.role === "family") {
        const ante = await api.get("/ante-mortem");
        setAnteList(ante.data);
      } else {
        const [post, ante, m, mi] = await Promise.all([
          api.get("/post-mortem"),
          api.get("/ante-mortem"),
          api.get("/matches/count"),
          api.get("/my-imports"),
        ]);
        setPostList(post.data);
        setAnteList(ante.data);
        setMatchCount(m.data.count);
        setMyImports(mi.data);
      }
    } catch (e) {
      // noop
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.role, user.status]);

  if (isPendingAuthority) {
    return <PendingApproval user={user} />;
  }

  const onDelete = async (kind, id) => {
    try {
      await api.delete(`/${kind === "ante_mortem" ? "ante-mortem" : "post-mortem"}/${id}`);
      toast.success("Record deleted");
      refresh();
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const isFamily = user.role === "family";
  const isAdmin = user.role === "admin";
  const isAuthority = !isFamily && !isAdmin;

  const withinDate = (c) => {
    if (dateFilter === "all") return true;
    if (!c.created_at) return false;
    const created = new Date(c.created_at);
    const cutoff = new Date();
    if (dateFilter === "today") {
      cutoff.setHours(0, 0, 0, 0);
    } else {
      cutoff.setDate(cutoff.getDate() - (dateFilter === "7d" ? 7 : 30));
    }
    return created >= cutoff;
  };
  const applyFilters = (list) =>
    list.filter((c) => (ownerFilter === "all" || c.owner_id === ownerFilter) && withinDate(c));
  const isMine = (c) => c.owner_id === user.id || (c.owner_ids || []).includes(user.id);
  const shownPost = applyFilters(mineOnly ? postList.filter(isMine) : postList);
  const shownAnte = applyFilters(mineOnly ? anteList.filter(isMine) : anteList);

  const canDeleteCase = (c) => isAdmin || c.owner_id === user.id;
  const toggleSelect = (kind, id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      const k = `${kind}:${id}`;
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  const selectAll = (kind, list) => {
    const ids = list.filter(canDeleteCase).map((c) => `${kind}:${c.id}`);
    setSelected((prev) => {
      const n = new Set(prev);
      const allIn = ids.length > 0 && ids.every((k) => n.has(k));
      ids.forEach((k) => (allIn ? n.delete(k) : n.add(k)));
      return n;
    });
  };
  const onBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(t("dash.bulk_confirm"))) return;
    const items = [...selected].map((k) => {
      const [kind, id] = k.split(":");
      return { kind, id };
    });
    try {
      const r = await api.post("/admin/cases/bulk-delete", { items });
      toast.success(`${r.data.deleted} ${t("dash.bulk_deleted")}`);
      setSelected(new Set());
      setSelectMode(false);
      refresh();
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-10" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-body text-xs uppercase tracking-[0.18em] text-[#6B7F82]">
            {isFamily ? t("dash.workspace.family") : isAdmin ? t("dash.workspace.admin") : t("dash.workspace.authority")}
          </p>
          <h1 className="mt-1 font-heading text-3xl font-semibold text-[#1A2024]">
            {t("dash.welcome")} {user.name}.
          </h1>
          <p className="mt-2 font-body text-sm text-[#5C6873]">
            {isFamily ? t("dash.family_sub") : isAdmin ? t("dash.admin_sub") : t("dash.authority_sub")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isFamily && (
            <Link to="/ante-mortem/new" data-testid="dashboard-new-ante-btn">
              <Button className="bg-[#2E3A46] text-white hover:bg-[#1A2024]"><Plus size={16} className="mr-1" /> {t("dash.new_ante")}</Button>
            </Link>
          )}
          {!isFamily && !isAdmin && (
            <Link to="/cases/new" data-testid="dashboard-new-case-btn">
              <Button className="bg-[#2E3A46] text-white hover:bg-[#1A2024]"><Plus size={16} className="mr-1" /> {t("dash.new_case")}</Button>
            </Link>
          )}
          {isAdmin && (
            <Link to="/admin/authorities" data-testid="dashboard-admin-btn">
              <Button className="bg-[#2E3A46] text-white hover:bg-[#1A2024]">
                <ShieldCheck size={16} className="mr-1" /> {t("dash.review_auth")}
                {pendingCount > 0 && (
                  <span className="ml-2 rounded-full bg-[#C99B4B] px-2 py-0.5 text-[10px] font-semibold text-white">
                    {pendingCount}
                  </span>
                )}
              </Button>
            </Link>
          )}
          {!isFamily && (
            <Button
              variant="ghost"
              onClick={() => { setSelectMode((v) => !v); setSelected(new Set()); }}
              className={selectMode ? "bg-[#9E4747] text-white hover:bg-[#7d3737] hover:text-white" : "text-[#8A96A0] hover:text-[#9E4747]"}
              data-testid="dashboard-bulk-toggle"
              aria-pressed={selectMode}
            >
              <Trash2 size={16} className="mr-1" /> {t("dash.bulk_select")}
            </Button>
          )}
          {isAuthority && (
            <div className="inline-flex overflow-hidden rounded-md border border-[#E2E8F0]" data-testid="dashboard-mine-toggle">
              <button
                type="button"
                onClick={() => setMineOnly(false)}
                className={`px-3 py-2 font-body text-sm transition-colors ${!mineOnly ? "bg-[#2E3A46] text-white" : "bg-white text-[#2E3A46] hover:bg-[#F8F9FA]"}`}
                data-testid="dashboard-filter-all"
                aria-pressed={!mineOnly}
              >
                {t("dash.all_reports")}
              </button>
              <button
                type="button"
                onClick={() => setMineOnly(true)}
                className={`px-3 py-2 font-body text-sm transition-colors ${mineOnly ? "bg-[#2E3A46] text-white" : "bg-white text-[#2E3A46] hover:bg-[#F8F9FA]"}`}
                data-testid="dashboard-filter-mine"
                aria-pressed={mineOnly}
              >
                {t("dash.mine_only")}
              </button>
            </div>
          )}
          <Link to="/matches" data-testid="dashboard-matches-link">
            <Button variant="outline" className="border-[#E2E8F0] text-[#2E3A46]">
              {isFamily ? (
                <><ShieldCheck size={16} className="mr-1" /> {t("dash.case_status")}</>
              ) : (
                <><Sparkles size={16} className="mr-1" /> {t("dash.matches")} ({matchCount})</>
              )}
            </Button>
          </Link>
        </div>
      </div>

      {isAdmin && (
        <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="admin-case-filters">
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="w-64 border-[#E2E8F0] bg-white" data-testid="filter-contributor">
              <SelectValue placeholder={t("dash.filter_contributor")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("dash.filter_all_users")}</SelectItem>
              {accounts.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.full_name || a.email} ({a.case_count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="w-44 border-[#E2E8F0] bg-white" data-testid="filter-date">
              <SelectValue placeholder={t("dash.filter_added")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("dash.filter_any_time")}</SelectItem>
              <SelectItem value="today">{t("dash.filter_today")}</SelectItem>
              <SelectItem value="7d">{t("dash.filter_7d")}</SelectItem>
              <SelectItem value="30d">{t("dash.filter_30d")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {!isFamily && (
        <div className="mt-8 flex flex-wrap items-center gap-5 rounded-xl border border-[#E2E8F0] bg-gradient-to-r from-[#F2F6F3] to-white p-5 shadow-sm" data-testid="eurofind-brand-card">
          <img
            src="/eurofind-logo.png"
            alt="EuroFind"
            className="h-20 w-20 flex-shrink-0 rounded-full ring-1 ring-black/5"
            data-testid="eurofind-logo-dashboard"
          />
          <div className="min-w-0 flex-1">
            <h2 className="font-heading text-lg font-semibold text-[#1A2024]">{t("dash.brand_title")}</h2>
            <p className="mt-1 max-w-xl font-body text-sm text-[#5C6873]">
              {t("dash.brand_body")}
            </p>
          </div>
          <a href="/eurofind-logo.png" download="EuroFind-logo.png" data-testid="eurofind-logo-download-btn">
            <Button className="bg-[#2E3A46] text-white hover:bg-[#1A2024]">
              <Download size={16} className="mr-1" /> {t("dash.download_logo")}
            </Button>
          </a>
          <a href="/eurofind-logo-transparent.png" download="EuroFind-logo-transparent.png" data-testid="eurofind-logo-transparent-download-btn">
            <Button variant="outline" className="border-[#E2E8F0] text-[#2E3A46]">
              <Download size={16} className="mr-1" /> {t("dash.download_logo_transparent")}
            </Button>
          </a>
        </div>
      )}

      <div className="mt-10 grid gap-4 sm:grid-cols-3">
        <StatBox label={isFamily ? t("dash.your_reports") : t("dash.stat_ante_all")} value={anteList.length} accent="#4A7C59" testid="stat-ante" />
        <StatBox label={t("dash.stat_post")} value={isFamily ? "—" : postList.length} accent="#1F3A5F" testid="stat-post" />
        <StatBox label={isFamily ? t("dash.case_status") : t("dash.stat_matches")} value={isFamily ? t("dash.monitored") : matchCount} testid="stat-matches" />
      </div>

      {!isFamily && !isAdmin && myImports && (
        <div className="mt-6 rounded-lg border border-[#E2E8F0] bg-[#F8F9FA] p-5" data-testid="my-imports-card">
          <p className="font-body text-xs uppercase tracking-[0.16em] text-[#6B7F82]">{t("dash.my_imports")}{myImports.organization ? ` · ${myImports.organization}` : ""}</p>
          <p className="mt-1 font-body text-sm text-[#5C6873]">
            <span className="font-heading text-xl font-semibold text-[#1A2024]" data-testid="my-imports-total">{myImports.total}</span> imported case{myImports.total === 1 ? "" : "s"}
            <span className="text-[#6B7F82]"> · {myImports.post_imported} unidentified · {myImports.ante_imported} missing</span>
            {myImports.last_import_at && <span className="text-[#6B7F82]"> · last {new Date(myImports.last_import_at).toLocaleDateString()}</span>}
          </p>
        </div>
      )}

      <section className="mt-10">
        <h2 className="font-heading text-xl font-semibold text-[#1A2024]">
          {isFamily ? t("dash.your_reports") : t("dash.your_post")}
        </h2>
        <div className="mt-4 space-y-3">
          {isFamily &&
            (anteList.length === 0 ? (
              <EmptyState
                title={t("dash.no_reports")}
                text={t("dash.no_reports_sub")}
                cta={
                  <Link to="/ante-mortem/new"><Button className="bg-[#2E3A46] text-white hover:bg-[#1A2024]">{t("dash.new_ante")}</Button></Link>
                }
              />
            ) : (
              anteList.map((c) => (
                <CaseCard key={c.id} c={c} kind="ante_mortem" canDelete onDelete={(id) => onDelete("ante_mortem", id)} />
              ))
            ))}

          {!isFamily &&
            (shownPost.length === 0 ? (
              <EmptyState
                title={t("dash.no_post")}
                text={t("dash.no_post_sub")}
                cta={
                  <Link to="/post-mortem/new"><Button className="bg-[#2E3A46] text-white hover:bg-[#1A2024]">{t("dash.new_post")}</Button></Link>
                }
              />
            ) : (
              <>
                {selectMode && (
                  <button
                    onClick={() => selectAll("post", shownPost)}
                    className="font-body text-xs font-medium text-[#2E3A46] underline hover:text-[#1A2024]"
                    data-testid="select-all-post"
                  >
                    {t("dash.select_all")}
                  </button>
                )}
                {shownPost.map((c) => (
                  <CaseCard
                    key={c.id}
                    c={c}
                    kind="post_mortem"
                    canDelete={canDeleteCase(c)}
                    onDelete={(id) => onDelete("post_mortem", id)}
                    selectMode={selectMode}
                    isSelected={selected.has(`post:${c.id}`)}
                    onToggleSelect={(id) => toggleSelect("post", id)}
                  />
                ))}
              </>
            ))}
        </div>
      </section>

      {!isFamily && (
        <section className="mt-12">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl font-semibold text-[#1A2024]">{t("dash.ante_all")}</h2>
            <span className="font-body text-sm text-[#5C6873]"><Users size={14} className="mb-0.5 mr-1 inline" />{shownAnte.length} {t("dash.reports_count")}</span>
          </div>
          <div className="mt-4 space-y-3">
            {shownAnte.length === 0 && (
              <EmptyState title={t("dash.ante_empty")} text={t("dash.ante_empty_sub")} />
            )}
            {selectMode && shownAnte.length > 0 && (
              <button
                onClick={() => selectAll("ante", shownAnte)}
                className="font-body text-xs font-medium text-[#2E3A46] underline hover:text-[#1A2024]"
                data-testid="select-all-ante"
              >
                {t("dash.select_all")}
              </button>
            )}
            {shownAnte.map((c) => (
              <CaseCard
                key={c.id}
                c={c}
                kind="ante_mortem"
                canDelete={canDeleteCase(c)}
                onDelete={(id) => onDelete("ante_mortem", id)}
                selectMode={selectMode}
                isSelected={selected.has(`ante:${c.id}`)}
                onToggleSelect={(id) => toggleSelect("ante", id)}
              />
            ))}
          </div>
        </section>
      )}

      {selectMode && (
        <div className="fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-full border border-[#E2E8F0] bg-white px-5 py-3 shadow-lg" data-testid="bulk-delete-bar">
          <span className="font-body text-sm text-[#1A2024]">
            <b>{selected.size}</b> {t("dash.selected")}
          </span>
          <Button
            size="sm"
            className="rounded-full bg-[#9E4747] text-white hover:bg-[#7d3737]"
            disabled={selected.size === 0}
            onClick={onBulkDelete}
            data-testid="bulk-delete-confirm-btn"
          >
            <Trash2 size={14} className="mr-1" /> {t("dash.delete_selected")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="rounded-full text-[#5C6873]"
            onClick={() => { setSelectMode(false); setSelected(new Set()); }}
            data-testid="bulk-delete-cancel-btn"
          >
            {t("dash.bulk_cancel")}
          </Button>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, testid, accent }) {
  return (
    <div className="rounded-lg border border-[#E2E8F0] bg-white p-5 shadow-sm" style={accent ? { borderTop: `3px solid ${accent}` } : undefined} data-testid={testid}>
      <div className="font-heading text-3xl font-semibold" style={{ color: accent || "#1A2024" }}>{value}</div>
      <div className="mt-1 font-body text-sm text-[#5C6873]">{label}</div>
    </div>
  );
}

function EmptyState({ title, text, cta }) {
  return (
    <div className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8F9FA] p-10 text-center" data-testid="empty-state">
      <h3 className="font-heading text-lg font-semibold text-[#1A2024]">{title}</h3>
      <p className="mx-auto mt-2 max-w-md font-body text-sm text-[#5C6873]">{text}</p>
      {cta && <div className="mt-4">{cta}</div>}
    </div>
  );
}
